import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, Label } from 'recharts';
import { Box, Typography, Paper } from '@mui/material';
import { Distribution } from '../../services/analyticsService';

interface DistributionChartProps {
  data: Distribution[];
  title: string;
}

const DistributionChart: React.FC<DistributionChartProps> = ({ data, title }) => {
  // Colores modernos por estado (en inglés)
  const COLORS: Record<string, string> = {
    completed: '#4caf50',
    draft: '#ff9800',
    in_progress: '#2196f3',
    cancelled: '#f44336',
  };

  // Traducción de estados para mostrar
  const STATUS_LABELS: Record<string, string> = {
    completed: 'Completada',
    draft: 'Borrador',
    in_progress: 'En Progreso',
    cancelled: 'Cancelada',
  };

  // Formatear datos para el gráfico
  const chartData = data.map((item) => ({
    name: STATUS_LABELS[item.status] || item.status,
    statusKey: item.status,
    value: parseInt(item.count),
    porcentaje: parseFloat(item.percentage),
  }));

  // Calcular total de auditorías
  const totalAuditorias = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0);
  }, [chartData]);

  // Componente de tooltip personalizado
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            📊 {data.name}
          </Typography>
          <Typography variant="h6" color="primary">
            {data.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.payload.porcentaje.toFixed(1)}% del total
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={350}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={110}
            paddingAngle={3}
            fill="#8884d8"
            dataKey="value"
            animationBegin={0}
            animationDuration={800}
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.statusKey] || '#9e9e9e'}
              />
            ))}
            
            {/* Etiqueta central con total */}
            <Label
              value={totalAuditorias.toString()}
              position="center"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                fill: '#1976d2',
              }}
            />
            <Label
              value="Total"
              position="center"
              dy={25}
              style={{
                fontSize: '14px',
                fill: '#666',
              }}
            />
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }}
            formatter={(value, entry: any) => `${value} (${entry.payload.value})`}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default DistributionChart;
